import { useEffect, useMemo, useState } from 'react';
import { useMutationObserver } from '../../hooks/useMutationObserver';
import InputFieldContainer from './InputFieldContainer';
import RadioButton from './RadioButton';
import DropDownList from './DropDownList';
import CountryCodeDropDownList from './CountryCodeDropDownList';
import CheckBox from './CheckBox';
import PasscodeVerifySection from '../mfa-verify/PasscodeVerifySection';
import UsernameField from './UsernameField';
import PhoneInputSection from '../enroll-phone/PhoneInputSection';
import SecretKeyInstructions from '../enroll-manual-push/SecretKeyInstructions';
import AutoPushCheckBox from './AutoPushCheckBox';
import PasswordSection from '../password-reset/PasswordSection';
import AuthenticatorMethodRadioButton from '../enroll-phone/AuthenticatorMethodRadioButton';

interface InputFieldContainerProps {
  inputFieldContainer: JQuery<HTMLElement>;
  className?: string;
}

const getErrorMessage = (inputContainer: JQuery) => {
  return inputContainer.find('.okta-form-input-error').text();
};

export default function InputFieldFactory({ inputFieldContainer, className }: Readonly<InputFieldContainerProps>) {
  const { inputContainer, input, dropDownList, form } = useMemo(() => {
    const inputContainer = inputFieldContainer.find('[data-se="o-form-input-container"]');
    const input = inputContainer.find('input');
    const form = inputFieldContainer.closest('form');
    const dropDownList = inputContainer.find('select');
    return { inputContainer, input, dropDownList, form };
  }, [ inputFieldContainer ]);

  const [ errorMessage, setErrorMessage ] = useState(getErrorMessage(inputContainer));
  const [ disabled, setDisabled ] = useState(input.prop('disabled') || form.hasClass('o-form-saving'));
  const [ hidden, setHidden ] = useState(
    inputFieldContainer[0].style.display === 'none' || inputFieldContainer.hasClass('hide')
  );
  const inputMutationObserverArgs = useMemo(() => {
    return {
      filterOptions: { attributeFilter: [ 'disabled' ] },
      onMutation: (mutation: MutationRecord) => {
        const targetElement = mutation.target as HTMLInputElement;
        setDisabled(targetElement.disabled);
      },
    };
  }, []);

  useEffect(() => {
    form.on('reset', () => {
      setErrorMessage('');
    });
  }, [ form ]);

  useMutationObserver({
    element: input[0],
    ...inputMutationObserverArgs,
  });

  useMutationObserver({
    element: form[0],
    filterOptions: { attributeFilter: [ 'class' ] },
    onMutation: () => {
      setDisabled(form.hasClass('o-form-saving'));
    },
  });

  const inputContainerMutationObserverArgs = useMemo(() => {
    return {
      filterOptions: { childList: true },
      onMutation: () => {
        setErrorMessage(getErrorMessage(inputContainer));
      },
    };
  }, [ inputContainer ]);

  useMutationObserver({
    element: inputContainer[0],
    ...inputContainerMutationObserverArgs,
  });

  const inputFieldContainerMutationObserverArgs = useMemo(() => {
    return {
      filterOptions: { attributeFilter: [ 'style' ] },
      onMutation: (mutation: MutationRecord) => {
        const targetElement = mutation.target as HTMLInputElement;
        setHidden(targetElement.style.display === 'none');
      },
    };
  }, []);

  useMutationObserver({
    element: inputFieldContainer[0],
    ...inputFieldContainerMutationObserverArgs,
  });

  function renderInputFieldComponent() {
    const props = {
      className,
      error: errorMessage,
      inputFieldContainer,
      disabled,
    };
    const isDropDownList = dropDownList.length;
    if (isDropDownList) {
      if ([ 'countryCode', 'country' ].includes(dropDownList[0].name)) {
        return <CountryCodeDropDownList {...props} />;
      }
      return <DropDownList {...props} />;
    }
    else {
      const usernameSelector = 'identifier';
      const passwordSelector = 'credentials.passcode';
      if (inputFieldContainer.find(`[name=${usernameSelector}]`).length) {
        return <UsernameField {...props} />;
      }
      else if (inputFieldContainer.find(`[name='${passwordSelector}']`).length) {
        return <PasswordSection {...props} />;
      }
      // Hide option to set up custom security question.
      else if (
        input.attr('type') === 'radio' &&
        inputContainer.find('[data-se="o-form-input-sub_schema_local_credentials"]').length
      ) {
        return <></>;
      }
      else if (input.attr('type') === 'radio') {
        const isAuthenticatorMethodRadioButton = inputFieldContainer.find('[data-se-name="authenticator.methodType"]').length > 0;
        return isAuthenticatorMethodRadioButton
          ? <AuthenticatorMethodRadioButton {...props} />
          : <RadioButton {...props} />;
      }
      else if (input.attr('name') === 'authenticator.autoChallenge' || input.attr('name') === 'autoChallenge') {
        return <AutoPushCheckBox {...props} />;
      }
      else if (input.attr('type') === 'checkbox') {
        return <CheckBox {...props} />;
      }
      else if (inputFieldContainer.hasClass('phone-authenticator-enroll__phone')) {
        return <PhoneInputSection {...props} />;
      }
      else if (inputFieldContainer.hasClass('phone-authenticator-enroll__phone-ext')) {
        //This component is managed by CallPhoneInputSection
        return <></>;
      }
      else if (inputFieldContainer.hasClass('auth-passcode')) {
        return <PasscodeVerifySection {...props} />;
      }
      else if (inputFieldContainer.hasClass('shared-secret')) {
        const container: JQuery<HTMLElement> = jQueryCourage('.o-form-fieldset-container');
        return <SecretKeyInstructions container={container} />;
      }
      return <InputFieldContainer {...props} />;
    }
  }

  if (!hidden) {
    return <>{renderInputFieldComponent()}</>;
  }
  else {
    return <></>;
  }
}
